import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  BillingAccountDetailView,
  BillingAccountSummaryView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link } from 'react-router-dom';
import { apiRequest } from './api';
import { formatMoney } from './billing';
import { PaymentForm, errorKeyOf, type ErrorKey } from './BillingForms';

export interface AthletePaymentSectionProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  athleteId: string;
}

/**
 * Records a payment from an athlete: finds the open billing accounts the athlete is on today and
 * posts to the chosen one through the same two-step form as the account page.
 */
export function AthletePaymentSection({
  locale,
  organizationId,
  csrfToken,
  athleteId,
}: AthletePaymentSectionProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing`;
  const [accounts, setAccounts] = useState<BillingAccountSummaryView[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [account, setAccount] = useState<BillingAccountDetailView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    apiRequest<BillingAccountSummaryView[]>(`${base}/athletes/${athleteId}/accounts`).then(
      (found) => {
        const open = found.filter((candidate) => candidate.status === 'active');
        setAccounts(open);
        setAccountId(open[0]?.id ?? '');
      },
      (error: unknown) => {
        setErrorKey(errorKeyOf(error));
      },
    );
  }, [base, athleteId]);

  const loadAccount = useCallback(
    async (id: string) => {
      try {
        setAccount(await apiRequest<BillingAccountDetailView>(`${base}/accounts/${id}`));
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    setAccount(null);
    if (accountId !== '') void loadAccount(accountId);
  }, [accountId, loadAccount]);

  const errorAlert = errorKey !== null && (
    <Alert severity="error" sx={{ mb: 2 }}>
      {translate(locale, errorKey)}
    </Alert>
  );

  if (accounts === null) {
    return (
      <>
        {errorAlert}
        {errorKey === null && (
          <Typography>{translate(locale, 'athletes.payment.loading')}</Typography>
        )}
      </>
    );
  }
  if (accounts.length === 0) {
    return (
      <>
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate(locale, 'athletes.payment.noAccount')}
        </Alert>
        <Button component={Link} to="/billing" variant="outlined">
          {translate(locale, 'athletes.payment.goToBilling')}
        </Button>
      </>
    );
  }
  return (
    <>
      {errorAlert}
      {recorded && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'athletes.payment.recorded')}
        </Alert>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, mb: 2 }}
      >
        {accounts.length > 1 && (
          <TextField
            select
            size="small"
            label={translate(locale, 'athletes.payment.account')}
            value={accountId}
            onChange={(event) => {
              setRecorded(false);
              setAccountId(event.target.value);
            }}
            sx={{ minWidth: 240 }}
          >
            {accounts.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {account !== null && (
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{account.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'billing.position.balance')}:{' '}
              {formatMoney(account.position.balanceMinor, account.currency, locale)} ·{' '}
              {translate(locale, 'billing.position.overdue')}:{' '}
              {formatMoney(account.position.overdueMinor, account.currency, locale)} ·{' '}
              {translate(locale, 'billing.position.credit')}:{' '}
              {formatMoney(account.position.unallocatedCreditMinor, account.currency, locale)}
            </Typography>
          </Box>
        )}
        {account !== null && (
          <Button component={Link} to={`/billing/accounts/${account.id}`} size="small">
            {translate(locale, 'athletes.payment.openAccount')}
          </Button>
        )}
      </Stack>
      {account !== null && (
        <PaymentForm
          key={account.id}
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          account={account}
          charges={account.charges}
          onPosted={async () => {
            setRecorded(true);
            await loadAccount(account.id);
          }}
          onError={(key) => {
            setErrorKey(key);
            if (key !== null) setRecorded(false);
          }}
        />
      )}
    </>
  );
}
